import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import {
  Checkbox,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
} from '@material-ui/core';
import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { IFilterOptions } from 'interfaces/SelectOptions';

import useStyles from './style';
import { IMultipleCheckboxWithSubgroup } from './types';

const MultipleCheckboxWithSubgroup = ({
  items,
  label,
  loading,
  selectAllLabel,
  noOptionsText,
  groupName,
  itemName,
  id,
  onFilterChange,
}: IMultipleCheckboxWithSubgroup) => {
  const classes = useStyles();

  const [open, setOpen] = useState<boolean>(false);
  const [filter, setFilter] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<IFilterOptions[]>([]);
  const [childrenSelectedOptions, setChildrenSelectedOptions] = useState<
    IFilterOptions[]
  >([]);
  const [textFieldRef, setTextFieldRef] = useState<HTMLInputElement | null>(
    null,
  );

  useLayoutEffect(() => {
    const closeOnScroll = () => {
      // const multipleCheckbox = document.getElementById('multiple-checkbox');
      // const isScrollInMultipleCheckbox = Boolean(
      //   event.path.find(element => element === multipleCheckbox),
      // );
      // TODO: necessita de correção, os componentes de gráfico estão chamando o evento
      // if (open && !isScrollInMultipleCheckbox) setOpen(false);
    };

    const divRoot = document.getElementById('root') as HTMLElement;
    if (divRoot) divRoot.addEventListener('scroll', closeOnScroll, true);
    return () => {
      if (divRoot) divRoot.removeEventListener('scroll', closeOnScroll);
    };
  }, [open]);

  const allSelected = React.useMemo(
    () => items?.length === selectedOptions?.length,
    [items?.length, selectedOptions?.length],
  );

  const hasSelectedItems = React.useMemo(
    () => selectedOptions?.length > 0 || childrenSelectedOptions?.length > 0,
    [selectedOptions?.length, childrenSelectedOptions?.length],
  );

  const qtdSelectedItems = React.useMemo(() => {
    /**
     * Para saber a quantidade de itens selecionados
     * basta somar a quandiade de itens presentes no
     * 'selectedOptions' e no 'childrenSelectedOptions'.
     */
    let qtdItems = 0;

    if (hasSelectedItems) {
      // É necessário usar um Set para garantir que não há
      // itens repetidos dentro das listas de itens selecionados.

      const uniqueSelectedItems = [
        ...new Set(selectedOptions.map(item => item.value)),
      ];
      const uniqueChildrenSelectedItems = [
        ...new Set(childrenSelectedOptions.map(item => item.value)),
      ];
      qtdItems =
        uniqueSelectedItems?.length + uniqueChildrenSelectedItems?.length;
    }

    return qtdItems;
  }, [childrenSelectedOptions, hasSelectedItems, selectedOptions]);

  const filteredItems = useMemo(() => {
    const filtered = items?.filter(item =>
      item.children?.find(child =>
        child.label.toLowerCase().includes(filter.toLowerCase()),
      ),
    );

    if (filtered && filtered.length) {
      return filtered;
    }

    return items?.filter(item =>
      item.label.toLowerCase().includes(filter.toLowerCase()),
    );
  }, [items, filter]);

  const hasFilteredItems = React.useMemo(
    () => filteredItems?.length > 0,
    [filteredItems],
  );

  const textInputValue = React.useMemo(() => {
    // se a lista estiver aberta ou nenhum item estiver selecionado
    if (open || !hasSelectedItems) return filter;

    // caso contrário
    return qtdSelectedItems === 1
      ? `${qtdSelectedItems} Item`
      : `${qtdSelectedItems} Itens`;
  }, [filter, hasSelectedItems, open, qtdSelectedItems]);

  useEffect(() => {
    onFilterChange({ name: groupName, value: selectedOptions });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions]);

  useEffect(() => {
    onFilterChange({ name: itemName, value: childrenSelectedOptions });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childrenSelectedOptions]);

  const handleChangeFilter = useCallback(e => {
    setFilter(e.target.value);
  }, []);

  const handleChange = useCallback(
    (event, checked = false) => {
      const {
        target: { name, value },
      } = event;
      if (name === 'select-all') {
        if (checked) {
          setSelectedOptions(items);
          setChildrenSelectedOptions(
            (items.map(item => item.children as IFilterOptions[]) || []).flat(),
          );
        } else {
          setSelectedOptions([]);
          setChildrenSelectedOptions([]);
        }
      } else if (name === groupName) {
        const foundItem = items.find(
          item => +item.value === +value,
        ) as IFilterOptions;

        if (foundItem) {
          if (checked) {
            setSelectedOptions([...selectedOptions, { ...foundItem }]);
            setChildrenSelectedOptions(prev => {
              const result = prev.filter(
                item =>
                  !foundItem.children?.some(
                    childrenItem => item.value === childrenItem.value,
                  ),
              );

              return [...result.concat(foundItem.children || [])];
            });
          } else {
            setSelectedOptions(prev => [
              ...prev.filter(v => v.value !== foundItem.value),
            ]);
            const saveChildrenSelected = [...childrenSelectedOptions];

            foundItem?.children?.forEach(fi => {
              saveChildrenSelected.splice(saveChildrenSelected.indexOf(fi), 1);
            });

            setChildrenSelectedOptions([...saveChildrenSelected]);
          }
        }
      } else {
        const childrenItems = items.map(item => item.children).flat();
        const childrenFound = childrenItems.find(
          item => item?.value === +value,
        ) as IFilterOptions;
        const foundParent = items.find(item =>
          item.children?.find(child => child.value === childrenFound.value),
        ) as IFilterOptions;

        if (checked) {
          setChildrenSelectedOptions([
            ...childrenSelectedOptions,
            childrenFound,
          ]);

          if (foundParent && foundParent.children) {
            const childsSelected = childrenSelectedOptions.filter(childrenOp =>
              foundParent.children?.includes(childrenOp),
            ).length;

            if (childsSelected === foundParent.children.length - 1) {
              setSelectedOptions([...selectedOptions, { ...foundParent }]);
            }
          }
        } else {
          setChildrenSelectedOptions(prev => [
            ...prev?.filter(v => v.value !== childrenFound.value),
          ]);

          if (foundParent) {
            setSelectedOptions(prev => [
              ...prev?.filter(v => v.value !== foundParent.value),
            ]);
          }
        }
      }
    },
    [childrenSelectedOptions, groupName, items, selectedOptions],
  );

  const handleGroupItemClick = React.useCallback(
    (name: string, value: string | number) => {
      return () => {
        const itemValue = selectedOptions.find(
          option => option.value === value,
        );
        handleChange({ target: { name, value } }, !itemValue);
      };
    },
    [handleChange, selectedOptions],
  );

  const handleItemClick = React.useCallback(
    (name: string, value: string | number) => {
      return () => {
        const itemValue = childrenSelectedOptions.find(
          option => option.value === value,
        );
        handleChange({ target: { name, value } }, !itemValue);
      };
    },
    [childrenSelectedOptions, handleChange],
  );

  const renderEndAdornment = React.useCallback(
    () => (
      <div className={classes.adornment}>
        {loading ? (
          <CircularProgress
            aria-label="autocomplete-loading"
            color="inherit"
            size={25}
          />
        ) : null}
        {open ? (
          <IconButton
            size="small"
            disabled={loading}
            className={classes.iconColor}
          >
            <ArrowDropUp />
          </IconButton>
        ) : (
          <IconButton
            size="small"
            disabled={loading}
            onClick={() => textFieldRef?.focus()}
            className={classes.iconColor}
          >
            <ArrowDropDown />
          </IconButton>
        )}
      </div>
    ),
    [classes.adornment, classes.iconColor, loading, open, textFieldRef],
  );

  const renderSelectAllItem = React.useCallback(() => {
    if (!selectAllLabel || !hasFilteredItems) return null;

    return (
      <ListItem
        className={classes.listItem}
        button
        onClick={
          () =>
            handleChange(
              {
                target: { name: 'select-all', value: 'select-all' },
              },
              !allSelected,
            )
          // eslint-disable-next-line react/jsx-curly-newline
        }
      >
        <ListItemIcon>
          <Checkbox
            name="select-all"
            checked={allSelected}
            onChange={handleChange}
            onClick={event => event.stopPropagation()}
            color="primary"
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
          />
        </ListItemIcon>
        <ListItemText primary={selectAllLabel} />
      </ListItem>
    );
  }, [
    allSelected,
    classes.listItem,
    handleChange,
    selectAllLabel,
    hasFilteredItems,
  ]);

  const renderNoOptionsTextItem = React.useCallback(() => {
    if (hasFilteredItems || !noOptionsText) return null;

    return (
      <ListItem className={classes.listItemNoOptionsText}>
        <ListItemText primary={noOptionsText} />
      </ListItem>
    );
  }, [classes.listItemNoOptionsText, hasFilteredItems, noOptionsText]);

  const renderListItem = React.useCallback(
    item => {
      const hasChecked = !!selectedOptions.find(
        option => option.value === item.value,
      );
      return (
        <ListItem
          className={classes.listItem}
          button
          key={item.value}
          onClick={handleGroupItemClick(groupName, item.value)}
        >
          <ListItemIcon>
            <Checkbox
              color="primary"
              name={groupName}
              value={item.value}
              checked={hasChecked}
              onChange={handleChange}
              onClick={event => event.stopPropagation()}
              indeterminate={
                childrenSelectedOptions.length !== 0 &&
                childrenSelectedOptions.filter(childrenOp =>
                  item.children?.find(
                    child => child.value === childrenOp.value,
                  ),
                ).length > (item.children && []).length &&
                !hasChecked
              }
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
            />
          </ListItemIcon>
          <ListItemText id={item.label} primary={item.label} />
        </ListItem>
      );
    },
    [
      childrenSelectedOptions,
      classes.listItem,
      groupName,
      handleChange,
      handleGroupItemClick,
      selectedOptions,
    ],
  );

  const renderListOptions = React.useCallback(() => {
    if (!open) return null;

    return (
      <ClickAwayListener
        onClickAway={() => {
          setOpen(false);
          setFilter('');
        }}
      >
        <Paper elevation={1} className={classes.paper}>
          <List className={classes.list} onClick={() => textFieldRef?.focus()}>
            {renderSelectAllItem()}
            {renderNoOptionsTextItem()}
            {filteredItems?.map(item => (
              <>
                {renderListItem(item)}
                {item.children?.map(child => (
                  <ListItem
                    button
                    className={classes.listItem}
                    style={{
                      paddingLeft: 48,
                    }}
                    key={child.value}
                    onClick={handleItemClick(itemName, child.value)}
                  >
                    <ListItemIcon>
                      <Checkbox
                        color="primary"
                        name={itemName}
                        value={child.value}
                        checked={
                          !!childrenSelectedOptions.find(
                            c => c?.value === child.value,
                          )
                        }
                        onChange={handleChange}
                        onClick={event => event.stopPropagation()}
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                      />
                    </ListItemIcon>
                    <ListItemText id={child.label} primary={child.label} />
                  </ListItem>
                ))}
              </>
            ))}
          </List>
        </Paper>
      </ClickAwayListener>
    );
  }, [
    childrenSelectedOptions,
    classes.list,
    classes.listItem,
    classes.paper,
    filteredItems,
    handleChange,
    handleItemClick,
    itemName,
    open,
    renderListItem,
    renderNoOptionsTextItem,
    renderSelectAllItem,
    textFieldRef,
  ]);

  return (
    <div id="multiple-checkbox">
      <TextField
        size="small"
        aria-autocomplete="none"
        className={classes.root}
        InputLabelProps={{
          className: classes.inputLabel,
        }}
        label={label}
        id={id}
        variant="outlined"
        onClick={() => (!loading ? setOpen(!open) : null)}
        value={textInputValue}
        onChange={handleChangeFilter}
        inputRef={element => {
          setTextFieldRef(element);
        }}
        disabled={loading || !items?.length}
        InputProps={{
          endAdornment: renderEndAdornment(),
          style: { paddingRight: 6 },
        }}
      />
      {renderListOptions()}
    </div>
  );
};

export default MultipleCheckboxWithSubgroup;
